import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DragDropContext,
    Draggable,
    Droppable,
    type DraggableProvided,
    type DraggableStateSnapshot,
    type DroppableProvided,
    type DropResult,
} from 'react-beautiful-dnd';

/** `react-beautiful-dnd` 与 React 18 类型声明不兼容，经 unknown 断言为 FC 供 JSX 使用（与 menu-library-menu-detail 一致） */
const CourseGroupTabDndContext = DragDropContext as unknown as React.FC<{
    onDragEnd: (result: DropResult) => void;
    children: React.ReactNode;
}>;
const CourseGroupTabDroppable = Droppable as unknown as React.FC<{
    droppableId: string;
    direction?: 'horizontal' | 'vertical';
    children: (provided: DroppableProvided) => React.ReactNode;
}>;
const CourseGroupTabDraggable = Draggable as unknown as React.FC<{
    draggableId: string;
    index: number;
    isDragDisabled?: boolean;
    children: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => React.ReactNode;
}>;
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import _ from 'lodash';
import clsx from 'clsx';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import {
    buildCloudDishCourseDetailFromMenuDish,
    buildCloudDishCourseGroupsFromUi,
    cloudCourseGroupsToUiDraft,
    type CloudCourseGroupUiDraft,
    type CloudDishLibraryCourseGroup,
} from '../../request/api/rbp/cloud-dish-library';
import { CourseDetail, CourseGroup, FirstMenu, MenuDish } from '../../models';
import GlobalNotification from '../../component/common/snackbar';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import { AntSwitch } from '../../component/switch/IOSSwitch';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import MenuDishList from '../dish-menu-creator/menu-dish-list/menu-dish-list';
import type { MenuListItem } from '../dish-menu-creator/menu-list/menu-list-component';
import { MenuLibraryCourseGroupDetailTable } from './menu-library-course-group-detail-table.component';
import DialogHeaderComponent from '../../component/dialog/dialog-header.component';
import './scss/menu-library-course-group-dialog.style.scss';
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as CloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { resolveActiveIdxAfterCourseGroupRemove } from '../../utils/menu-library-course-group';

function MenuLibraryCourseGroupDialogSkeleton() {
    const sk = (w: string | number, h: number) => (
        <Skeleton variant="rounded" animation="wave" width={w} height={h} sx={{ borderRadius: '8px' }} />
    );
    return (
        <Box
            className="menu-library-course-group-dialog__skeleton"
            aria-busy="true"
            aria-label={i18n.t('general_text_loading')}
        >
            <Paper elevation={0} className="menu-library-course-group-dialog__tab-bar menu-library-course-group-dialog__skeleton-tab-bar">
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Stack direction="row" spacing={1} flex={1} minWidth={0} useFlexGap>
                        {sk(120, 40)}
                        {sk(100, 40)}
                        {sk(100, 40)}
                    </Stack>
                    {sk(100, 36)}
                </Stack>
            </Paper>
            <Paper elevation={0} className="menu-library-course-group-dialog__form-row menu-library-course-group-dialog__skeleton-form">
                <Box className="menu-library-course-group-dialog__form-row-grid">
                    <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                        {sk('100%', 20)}
                        {sk('100%', 40)}
                    </Box>
                    <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                        {sk('100%', 20)}
                        {sk('100%', 40)}
                    </Box>
                    <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                        {sk('100%', 20)}
                        {sk('100%', 40)}
                    </Box>
                    <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                        {sk('60%', 20)}
                        {sk('100%', 28)}
                    </Box>
                    <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                        {sk('60%', 20)}
                        {sk('100%', 28)}
                    </Box>
                </Box>
            </Paper>
            <Paper elevation={0} className="menu-library-course-group-dialog__table-card menu-library-course-group-dialog__skeleton-table-card">
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                    {sk(140, 32)}
                    {sk(200, 36)}
                </Stack>
                <Stack spacing={1} className="menu-library-course-group-dialog__skeleton-table-rows">
                    {sk('100%', 44)}
                    {sk('100%', 44)}
                    {sk('100%', 44)}
                    {sk('100%', 44)}
                    {sk('100%', 44)}
                    {sk('100%', 44)}
                </Stack>
            </Paper>
        </Box>
    );
}

export type MenuLibraryCourseGroupDialogProps = {
    open: boolean;
    onClose: () => void;
    /**
     * 本地模式：套餐头菜品 `item_id`（须已落库 >0）。
     * 云端模式可传 0（改用 `cloudDishId`）。
     */
    dishItemId: number;
    /** 与 `dish-group-detail` 一致：用于「添加菜品」选择器回退数据 */
    menuData: FirstMenu[];
    /** 页面级预取的全店普通菜单（含菜品）；未拉到前可为 null（仅本地） */
    pickerMenusAll: FirstMenu[] | null;
    pickerMenusLoading: boolean;
    /** 菜单库模块当前门店 `string_id`；不传时套餐接口回退全局门店码 */
    rbpStringId?: string;
    /** 模块内所选店货币符号（符号在数字前） */
    moduleCurrencyName?: string;
    /**
     * `cloud`：草稿态编辑套餐组，保存时回调 `onCloudSave`（由父组件调 `dishes/update`）。
     * 默认 `local`：走本地 `post_course_group` / `post_course_detail` 等。
     */
    mode?: 'local' | 'cloud';
    /** 云端菜单 id（mode=cloud 时保存用） */
    cloudMenuId?: string;
    /** 云端套餐菜品 id（mode=cloud） */
    cloudDishId?: string;
    /** 详情回填的套餐组（mode=cloud） */
    initialCourseGroups?: CloudDishLibraryCourseGroup[] | null;
    /**
     * 云端保存：父组件用当前菜品表单字段 + 本参数组装 `dishes/update`。
     * @param courseGroups - 完整套餐组对象数组
     */
    onCloudSave?: (courseGroups: CloudDishLibraryCourseGroup[]) => Promise<void>;
    /** 云端选菜骨架（菜单列表，小类无菜或已 merge 详情） */
    cloudPickerMenus?: FirstMenu[] | null;
    /** 云端选菜列表 loading */
    cloudPickerMenusLoading?: boolean;
    /** 正在懒加载详情的云端菜单 id（展开行内 LoadingRow） */
    cloudPickerDetailLoadingId?: string | null;
    /**
     * 打开选菜弹窗时预取云端菜单列表（若尚未加载）。
     */
    onEnsureCloudPickerMenus?: () => void | Promise<void>;
    /**
     * 点击一级云端菜单时懒加载详情并 merge 进选菜树。
     * @param cloudMenuId - 云端菜单 string id
     */
    onLoadCloudPickerMenuDetail?: (cloudMenuId: string) => Promise<void>;
};

export function MenuLibraryCourseGroupDialog(props: MenuLibraryCourseGroupDialogProps) {
    const {
        open,
        onClose,
        dishItemId,
        menuData,
        pickerMenusAll,
        rbpStringId,
        moduleCurrencyName = '€',
        mode = 'local',
        initialCourseGroups = null,
        onCloudSave,
        cloudPickerMenus = null,
        cloudPickerMenusLoading = false,
        cloudPickerDetailLoadingId = null,
        onEnsureCloudPickerMenus,
        onLoadCloudPickerMenuDetail,
    } = props;

    /** 是否云端草稿模式 */
    const isCloudMode = mode === 'cloud';

    const [courseGroup, setCourseGroup] = useState<CloudCourseGroupUiDraft[]>([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedRowIdx, setSelectedRowIdx] = useState<number[]>([]);
    const [dishPickerOpen, setDishPickerOpen] = useState(false);
    /** 「新增菜品」弹窗内点击确认，post_course_detail / 刷新列表期间 */
    const [dishPickerConfirmLoading, setDishPickerConfirmLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(false);
    /** 底部「保存」patch_course_group / patchMenuItemFullSelect 请求中 */
    const [saveGroupLoading, setSaveGroupLoading] = useState(false);
    /** Tab 栏「新增套餐组」post_course_group 请求中 */
    const [addGroupLoading, setAddGroupLoading] = useState(false);
    /** 横向拖拽套餐组 Tab 后 PATCH menus/first/index（course_group_id + slu_priority） */
    const [reorderTabLoading, setReorderTabLoading] = useState(false);
    /** 批量删除套餐组明细请求中 */
    const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
    /** 行内删除中的明细 `detail_id`（仅该行展示 loading，其余行不禁用动画） */
    const [deletingDetailRowId, setDeletingDetailRowId] = useState<number | null>(null);
    /** 批量「移除所选」二次确认 */
    const [batchRemoveConfirmOpen, setBatchRemoveConfirmOpen] = useState(false);
    /** 删除整组二次确认 */
    const [deleteGroupConfirmOpen, setDeleteGroupConfirmOpen] = useState(false);
    /** 待删除套餐组在 `courseGroup` 中的下标 */
    const [deleteGroupTargetIdx, setDeleteGroupTargetIdx] = useState<number | null>(null);
    /** 删除整组请求中（本地调 API） */
    const [deleteGroupLoading, setDeleteGroupLoading] = useState(false);
    /** 云端草稿临时 id 递增（组 / 明细） */
    const cloudTempIdRef = useRef(1000);
    /** 可选数量：本地字符串，便于清空与连续键入；仅在切换套餐组 / 类型时与 activeGroup 同步 */
    const [chooseNumDraft, setChooseNumDraft] = useState('1');

    const activeGroup: CourseGroup | undefined = courseGroup[activeIdx];

    const chooseNumSyncKey = activeGroup
        ? `${activeIdx}-${String(activeGroup.course_group_id)}-${String(activeGroup.is_must)}`
        : '';

    /** 仅在「切换套餐组 / 类型」时同步可选数量草稿；courseGroup 因键入而变时 chooseNumSyncKey 不变，此处直接 return，不打断输入 */
    const chooseNumSyncKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (chooseNumSyncKeyRef.current === chooseNumSyncKey) {
            return;
        }
        chooseNumSyncKeyRef.current = chooseNumSyncKey;
        const g = courseGroup[activeIdx];
        if (g) {
            setChooseNumDraft(String(g.choose_num ?? 1));
        }
    }, [chooseNumSyncKey, courseGroup, activeIdx]);

    const loadCourseGroups = useCallback(async () => {
        /** 云端：用详情回填的 course_groups；无则建两个默认组草稿 */
        if (isCloudMode) {
            setInitLoading(true);
            setLoading(true);
            try {
                cloudTempIdRef.current = 1000;
                let groups = cloudCourseGroupsToUiDraft(initialCourseGroups);
                if (groups.length === 0) {
                    groups = [
                        {
                            course_group_id: ++cloudTempIdRef.current,
                            course_group_name: i18n.t('menu_and_dishes_page_required_group'),
                            is_must: 1,
                            choose_num: 1,
                            course_detail: [],
                        },
                        {
                            course_group_id: ++cloudTempIdRef.current,
                            course_group_name: i18n.t('menu_and_dishes_page_option_group'),
                            is_must: 0,
                            choose_num: 1,
                            course_detail: [],
                        },
                    ];
                } else {
                    const maxId = groups.reduce((m, g) => Math.max(m, Number(g.course_group_id) || 0), 0);
                    cloudTempIdRef.current = Math.max(cloudTempIdRef.current, maxId + 1);
                }
                setCourseGroup(_.cloneDeep(groups));
                const firstMandatory = groups.findIndex((g) => Number(g.is_must) === 1);
                setActiveIdx(firstMandatory >= 0 ? firstMandatory : 0);
            } finally {
                setInitLoading(false);
                setLoading(false);
            }
            return;
        }
        if (!dishItemId) {
            return;
        }
        setInitLoading(true);
        setLoading(true);
        try {
            const result = await apiManage_rbp.get_dish_course_by_id(dishItemId, rbpStringId);
            let groups: CourseGroup[] = [];
            if (!_.get(result, 'error') && Array.isArray(result)) {
                groups = (result as CourseGroup[]).filter((item) => item.menu_item_id === dishItemId);
            }
            if (groups.length === 0) {
                const params: CourseGroup[] = [
                    {
                        course_group_id: 0,
                        menu_item_id: dishItemId,
                        course_group_name: i18n.t('menu_and_dishes_page_required_group'),
                        is_must: 1,
                        choose_num: 1,
                    },
                    {
                        course_group_id: 0,
                        menu_item_id: dishItemId,
                        course_group_name: i18n.t('menu_and_dishes_page_option_group'),
                        is_must: 0,
                        choose_num: 1,
                    },
                ];
                for (let i = 0; i < params.length; i++) {
                    const row = params[i];
                    if (!row) {
                        continue;
                    }
                    const { course_group_id: _cid, menu_item_id: _mid, ...otherParams } = row;
                    void _cid;
                    void _mid;
                    const res = await apiManage_rbp.post_course_group(dishItemId, otherParams as CourseGroup, rbpStringId);
                    if (!_.get(res, 'error') && res.insertId) {
                        row.course_group_id = res.insertId;
                    }
                }
                groups = params;
            }
            setCourseGroup(_.cloneDeep(groups));
            const firstMandatory = groups.findIndex((g) => Number(g.is_must) === 1);
            setActiveIdx(firstMandatory >= 0 ? firstMandatory : 0);
        } catch (e) {
            console.error('loadCourseGroups', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setInitLoading(false);
            setLoading(false);
        }
    }, [dishItemId, rbpStringId, isCloudMode, initialCourseGroups]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!isCloudMode && !dishItemId) {
            return;
        }
        void loadCourseGroups();
        setSearchKeyword('');
        setSelectedRowIdx([]);
    }, [open, dishItemId, loadCourseGroups, isCloudMode]);

    /** 主弹窗关闭时收起二次确认，避免遮罩残留在已关闭的配置弹窗之上 */
    useEffect(() => {
        if (!open) {
            setBatchRemoveConfirmOpen(false);
            setDeleteGroupConfirmOpen(false);
            setDeleteGroupTargetIdx(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !courseGroup.length) {
            return;
        }
        if (activeIdx < 0 || activeIdx >= courseGroup.length) {
            setActiveIdx(0);
        }
    }, [open, courseGroup, activeIdx]);

    const updateGroupField = (patch: Partial<CourseGroup>) => {
        setCourseGroup((prev) => {
            const next = [...prev];
            const cur = next[activeIdx];
            if (!cur) {
                return prev;
            }
            next[activeIdx] = { ...cur, ...patch };
            return next;
        });
    };

    /** 套餐组 Tab 横向拖拽结束：本地改序并 PATCH `/v3/menus/first/index/{string_id}` */
    const handleCourseGroupTabDragEnd = useCallback(
        async (result: DropResult) => {
            const { source, destination } = result;
            if (!destination || (source.index === destination.index && source.droppableId === destination.droppableId)) {
                return;
            }
            const prevGroups = _.cloneDeep(courseGroup);
            const prevActiveIdx = activeIdx;
            const prevActiveId = courseGroup[activeIdx]?.course_group_id;
            const next = Array.from(courseGroup);
            const [moved] = next.splice(source.index, 1);
            if (moved == null) {
                return;
            }
            next.splice(destination.index, 0, moved);
            setCourseGroup(next);
            const newActiveIdx =
                prevActiveId != null
                    ? next.findIndex((x) => x.course_group_id === prevActiveId)
                    : Math.min(destination.index, Math.max(0, next.length - 1));
            setActiveIdx(newActiveIdx >= 0 ? newActiveIdx : 0);

            const payload = next
                .filter((g) => g.course_group_id != null && Number(g.course_group_id) > 0)
                .map((g, i) => ({ course_group_id: Number(g.course_group_id), slu_priority: i + 1 }));
            if (payload.length === 0) {
                return;
            }
            /** 云端草稿：只改本地顺序，slu_priority 在保存 course_groups 时带出 */
            if (isCloudMode) {
                setCourseGroup(
                    next.map((g, i) => ({
                        ...g,
                        slu_priority: i,
                    })),
                );
                return;
            }
            setReorderTabLoading(true);
            try {
                const res = await apiManage_rbp.patch_menus_first_index_course_group_priorities(payload, rbpStringId);
                if (_.get(res, 'error')) {
                    throw new Error('patch course group index failed');
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            } catch (e) {
                console.error('handleCourseGroupTabDragEnd', e);
                setCourseGroup(prevGroups);
                setActiveIdx(prevActiveIdx);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setReorderTabLoading(false);
            }
        },
        [courseGroup, activeIdx, rbpStringId, isCloudMode],
    );

    /**
     * 打开删除套餐组确认框（Tab 关闭图标触发）。
     * @param idx - 待删组下标
     */
    const openDeleteGroupConfirm = (idx: number) => {
        if (deleteGroupLoading || addGroupLoading || reorderTabLoading) {
            return;
        }
        setDeleteGroupTargetIdx(idx);
        setDeleteGroupConfirmOpen(true);
    };

    /**
     * 确认删除套餐组：本地调 `delete_course_group`；云端仅从草稿移除（随底部保存落库）。
     * 允许删光最后一组。
     */
    const handleDeleteGroupConfirm = async () => {
        const removeIdx = deleteGroupTargetIdx;
        if (removeIdx == null || removeIdx < 0 || removeIdx >= courseGroup.length) {
            setDeleteGroupConfirmOpen(false);
            setDeleteGroupTargetIdx(null);
            return;
        }
        if (deleteGroupLoading) {
            return;
        }
        const target = courseGroup[removeIdx];
        const groupId = Number(target?.course_group_id) || 0;
        setDeleteGroupLoading(true);
        try {
            if (!isCloudMode && groupId > 0) {
                const result = await apiManage_rbp.delete_course_group([groupId], rbpStringId);
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('google_text_delete_failed'));
                    return;
                }
            }
            setCourseGroup((prev) => prev.filter((_, i) => i !== removeIdx));
            setActiveIdx(resolveActiveIdxAfterCourseGroupRemove(activeIdx, removeIdx, courseGroup.length - 1));
            setSelectedRowIdx([]);
            setSearchKeyword('');
            setDeleteGroupConfirmOpen(false);
            setDeleteGroupTargetIdx(null);
        } catch (e) {
            console.error('handleDeleteGroupConfirm', e);
            GlobalNotification.error(i18n.t('google_text_delete_failed'));
        } finally {
            setDeleteGroupLoading(false);
        }
    };

    /** 待删组展示名（确认弹窗高亮） */
    const deleteGroupHighlightName = useMemo(() => {
        if (deleteGroupTargetIdx == null) {
            return '—';
        }
        const g = courseGroup[deleteGroupTargetIdx];
        return String(g?.course_group_name ?? '').trim() || '—';
    }, [courseGroup, deleteGroupTargetIdx]);

    const handleAddGroup = async () => {
        /** 列表不区分必选/可选入口时，新增默认「可选」组，可在表单中改为必选 */
        if (isCloudMode) {
            const newCourseGroup: CloudCourseGroupUiDraft = {
                course_group_id: ++cloudTempIdRef.current,
                menu_item_id: dishItemId || undefined,
                course_group_name: i18n.t('menu_and_dishes_page_option_group'),
                is_must: 0,
                choose_num: 1,
                course_detail: [],
            };
            setCourseGroup((prev) => {
                const next = [...prev, newCourseGroup];
                setActiveIdx(next.length - 1);
                return next;
            });
            return;
        }
        const newCourseGroup: CourseGroup = {
            menu_item_id: dishItemId,
            course_group_name: i18n.t('menu_and_dishes_page_option_group'),
            is_must: 0,
            choose_num: 1,
        };
        setAddGroupLoading(true);
        try {
            const result = await apiManage_rbp.post_course_group(dishItemId, newCourseGroup, rbpStringId);
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            newCourseGroup.course_group_id = result.insertId;
            setCourseGroup((prev) => {
                const next = [...prev, newCourseGroup];
                setActiveIdx(next.length - 1);
                return next;
            });
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch (e) {
            console.error('handleAddGroup', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setAddGroupLoading(false);
        }
    };

    const handleSaveGroup = async () => {
        /** 云端：整表校验后回调父组件 `dishes/update`（允许空数组，用于删光全部套餐组后落库） */
        if (isCloudMode) {
            for (const row of courseGroup) {
                if (!String(row.course_group_name ?? '').trim()) {
                    GlobalNotification.error(i18n.t('menu_library_course_group_name_required'));
                    return;
                }
            }
            if (!onCloudSave) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            setSaveGroupLoading(true);
            setLoading(true);
            try {
                const payload = buildCloudDishCourseGroupsFromUi(courseGroup);
                await onCloudSave(payload);
                GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
                onClose();
            } catch (e) {
                console.error('handleSaveGroup cloud', e);
                GlobalNotification.error(i18n.t('global_save_text_failed'));
            } finally {
                setLoading(false);
                setSaveGroupLoading(false);
            }
            return;
        }
        const g = courseGroup[activeIdx];
        if (!g?.course_group_id) {
            return;
        }
        if (!String(g.course_group_name ?? '').trim()) {
            GlobalNotification.error(i18n.t('menu_library_course_group_name_required'));
            return;
        }
        setSaveGroupLoading(true);
        setLoading(true);
        try {
            const params = {
                course_group_name: g.course_group_name,
                is_must: g.is_must,
                choose_num: g.choose_num,
            };
            const result = await apiManage_rbp.patch_course_group(
                dishItemId,
                g.course_group_id,
                params as CourseGroup,
                rbpStringId,
            );
            const result2 = await apiManage_rbp.patchMenuItemFullSelect(
                {
                    course_group_id: g.course_group_id,
                    is_full_select: g.is_full_select ? Number(g.is_full_select) : 0,
                    is_select_all: g.is_select_all ? Number(g.is_select_all) : 0,
                },
                rbpStringId,
            );
            if (!_.get(result, 'error') && !_.get(result2, 'error')) {
                GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
            } else {
                GlobalNotification.error(i18n.t('global_save_text_failed'));
            }
        } finally {
            setLoading(false);
            setSaveGroupLoading(false);
        }
    };

    const addDishesToGroup = async (dishes: MenuDish[]) => {
        if (!dishes.length) {
            setDishPickerOpen(false);
            return;
        }
        const g = courseGroup[activeIdx];
        if (!g || g.course_group_id == null || Number(g.course_group_id) <= 0) {
            return;
        }
        if (isCloudMode) {
            const newItems = dishes.map((item) => {
                const snapshot = buildCloudDishCourseDetailFromMenuDish(item);
                return {
                    detail_id: ++cloudTempIdRef.current,
                    menu_item_id: snapshot.source_menu_item_id,
                    item_name1: snapshot.item_name1,
                    item_course_name: snapshot.item_course_name,
                    unit: snapshot.unit,
                    num: snapshot.num,
                    price: snapshot.price,
                    description: snapshot.description,
                    item_description: snapshot.item_description,
                    rule_lnr_max_num: snapshot.rule_lnr_max_num,
                    cloud_snapshot: snapshot,
                };
            });
            setCourseGroup((prev) => {
                const next = [...prev];
                const cur = next[activeIdx];
                if (!cur) {
                    return prev;
                }
                next[activeIdx] = {
                    ...cur,
                    course_detail: [...(cur.course_detail || []), ...newItems],
                };
                return next;
            });
            setDishPickerOpen(false);
            return;
        }
        const newItems: CourseDetail[] = dishes.map((item) => ({
            item_name1: item.item_name1,
            menu_item_id: item.item_id,
            item_course_name: item.item_name1 || '',
            unit: item.unit_1 || '',
            num: 1,
            price: 0,
            description: '',
            rule_lnr_max_num: 9999,
        }));
        const preserveGroupId = g.course_group_id;
        const params = newItems.map((item) => {
            const { item_name1: _n, ...rest } = item;
            void _n;
            return rest;
        });
        setDishPickerConfirmLoading(true);
        setLoading(true);
        try {
            const result = await apiManage_rbp.post_course_detail(g.course_group_id, params as CourseDetail[], rbpStringId);
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            /** 重新拉取套餐组+明细，避免 insertId 批量插入与本地合并不一致导致列表不刷新 */
            const refreshed = await apiManage_rbp.get_dish_course_by_id(dishItemId, rbpStringId);
            if (!_.get(refreshed, 'error') && Array.isArray(refreshed)) {
                const groups = (refreshed as CourseGroup[]).filter((item) => item.menu_item_id === dishItemId);
                setCourseGroup(_.cloneDeep(groups));
                const nextIdx = groups.findIndex((x) => x.course_group_id === preserveGroupId);
                if (nextIdx >= 0) {
                    setActiveIdx(nextIdx);
                }
            }
            setDishPickerOpen(false);
        } finally {
            setLoading(false);
            setDishPickerConfirmLoading(false);
        }
    };

    const handleDeleteSelectedRows = async () => {
        if (deletingDetailRowId != null) {
            return;
        }
        const g = courseGroup[activeIdx];
        if (!g?.course_detail?.length || !selectedRowIdx.length) {
            return;
        }
        const rows = g.course_detail;
        /** selectedRowIdx 存的是 detail_id（与明细表勾选一致） */
        const toDelete = selectedRowIdx
            .map((detailId) => {
                const index = rows.findIndex((r) => r.detail_id === detailId);
                if (index < 0) {
                    return null;
                }
                return { id: detailId, index };
            })
            .filter((x): x is { id: number; index: number } => x != null);
        if (!toDelete.length) {
            return;
        }
        setDeleteSelectedLoading(true);
        try {
            if (isCloudMode) {
                const sorted = toDelete.sort((a, b) => b.index - a.index);
                setCourseGroup((prev) => {
                    const next = [...prev];
                    const gr = { ...next[activeIdx] };
                    const cd = [...(gr.course_detail || [])];
                    sorted.forEach(({ index }) => {
                        cd.splice(index, 1);
                    });
                    gr.course_detail = cd;
                    next[activeIdx] = gr;
                    return next;
                });
                setSelectedRowIdx([]);
                return;
            }
            const result = await apiManage_rbp.delete_course_detail(toDelete.map((x) => x.id), rbpStringId);
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('google_text_delete_failed'));
                return;
            }
            const sorted = toDelete.sort((a, b) => b.index - a.index);
            setCourseGroup((prev) => {
                const next = [...prev];
                const gr = { ...next[activeIdx] };
                const cd = [...(gr.course_detail || [])];
                sorted.forEach(({ index }) => {
                    cd.splice(index, 1);
                });
                gr.course_detail = cd;
                next[activeIdx] = gr;
                return next;
            });
            setSelectedRowIdx([]);
        } finally {
            setDeleteSelectedLoading(false);
        }
    };

    /** 行内删除单条明细：`delete_course_detail`，成功后同步本地列表与勾选 */
    const handleDeleteDetailRow = useCallback(async (detailId: number) => {
        if (detailId <= 0 || deleteSelectedLoading) {
            return;
        }
        setDeletingDetailRowId(detailId);
        try {
            if (isCloudMode) {
                setCourseGroup((prev) => {
                    const next = [...prev];
                    for (let gi = 0; gi < next.length; gi++) {
                        const gr = next[gi];
                        const cd = gr.course_detail;
                        if (!cd?.length) {
                            continue;
                        }
                        const idx = cd.findIndex((r) => r.detail_id === detailId);
                        if (idx >= 0) {
                            const gr2 = { ...gr, course_detail: [...cd] };
                            gr2.course_detail.splice(idx, 1);
                            next[gi] = gr2;
                            return next;
                        }
                    }
                    return prev;
                });
                setSelectedRowIdx((prev) => prev.filter((id) => id !== detailId));
                return;
            }
            const result = await apiManage_rbp.delete_course_detail([detailId], rbpStringId);
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('google_text_delete_failed'));
                return;
            }
            setCourseGroup((prev) => {
                const next = [...prev];
                for (let gi = 0; gi < next.length; gi++) {
                    const gr = next[gi];
                    const cd = gr.course_detail;
                    if (!cd?.length) {
                        continue;
                    }
                    const idx = cd.findIndex((r) => r.detail_id === detailId);
                    if (idx >= 0) {
                        const gr2 = { ...gr, course_detail: [...cd] };
                        gr2.course_detail.splice(idx, 1);
                        next[gi] = gr2;
                        return next;
                    }
                }
                return prev;
            });
            setSelectedRowIdx((prev) => prev.filter((id) => id !== detailId));
        } finally {
            setDeletingDetailRowId(null);
        }
    }, [deleteSelectedLoading, rbpStringId, isCloudMode]);

    /** 须依赖 courseGroup + activeIdx：仅依赖 course_detail 引用时，新增明细后若引用未变则 memo 不刷新 */
    const filteredDetails = useMemo(() => {
        const g = courseGroup[activeIdx];
        const list = g?.course_detail ?? [];
        const q = searchKeyword.trim().toLowerCase();
        if (!q) {
            return list.map((row) => ({ ...row }));
        }
        return list
            .map((row) => ({ ...row }))
            .filter(
                (row) =>
                    String(row.item_name1 ?? '')
                        .toLowerCase()
                        .includes(q) ||
                    String(row.item_course_name ?? '')
                        .toLowerCase()
                        .includes(q) ||
                    String(row.menu_item_id ?? '').includes(q),
            );
    }, [courseGroup, activeIdx, searchKeyword]);

    const currency = moduleCurrencyName.trim() || '€';

    const handleCourseDetailUpdated = useCallback(
        (detailId: number, patch: Pick<CourseDetail, 'price' | 'rule_lnr_max_num'>) => {
            setCourseGroup((prev) => {
                const next = [...prev];
                const gr = next[activeIdx];
                if (!gr) {
                    return prev;
                }
                const cd = (gr.course_detail || []).map((d) =>
                    d.detail_id === detailId ? { ...d, ...patch } : d,
                );
                next[activeIdx] = { ...gr, course_detail: cd };
                return next;
            });
        },
        [activeIdx],
    );

    /** 全菜单优先，未拉到则回退父组件传入（单菜单）；云端用懒加载骨架 */
    const menuDataForPicker = isCloudMode
        ? cloudPickerMenus ?? []
        : pickerMenusAll ?? menuData;

    /**
     * 云端选菜：点击一级菜单 → 拉详情并 merge。
     * @param menu - 列表节点
     */
    const handleCloudPickerExpandFirstMenu = useCallback(
        async (menu: MenuListItem) => {
            if (!isCloudMode || !onLoadCloudPickerMenuDetail) {
                return;
            }
            const ds = menu.dataSource as { id?: string } | undefined;
            const cloudMenuId = String(ds?.id ?? '').trim();
            if (!cloudMenuId) {
                return;
            }
            await onLoadCloudPickerMenuDetail(cloudMenuId);
        },
        [isCloudMode, onLoadCloudPickerMenuDetail],
    );

    /** BaseInput 的 MenuItem 使用 `i18n.t(option.label)`，label 须为 i18n key */
    const typeSelectItems = useMemo(
        () => [
            { value: '1', label: 'menu_and_dishes_page_required' },
            { value: '0', label: 'menu_and_dishes_page_optional' },
        ],
        [],
    );

    const courseGroupTypeSelectProps = useMemo(
        () => ({
            MenuProps: {
                sx: { zIndex: 2000 },
            },
            /** Figma：下拉箭头为主题紫 */
            sx: {
                '& .MuiSvgIcon-root': {
                    color: 'var(--fill-primary-300)',
                },
            },
        }),
        [],
    );

    const courseGroupNameInputProps = useMemo(
        () => ({
            sx: {
                borderRadius: '6px',
                '& .MuiOutlinedInput-input': {
                    padding: '8px',
                },
            },
        }),
        [],
    );

    const courseGroupTypeInputProps = useMemo(
        () => ({
            sx: {
                borderRadius: '8px',
                '& .MuiOutlinedInput-input': {
                    padding: '8px 16px',
                },
            },
        }),
        [],
    );

    const courseGroupChooseNumInputProps = useMemo(
        () => ({
            sx: {
                borderRadius: '8px',
                '& .MuiOutlinedInput-input': {
                    padding: '8px 16px',
                    textAlign: 'center' as const,
                },
            },
        }),
        [],
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                scroll="paper"
                className="menu-library-course-group-dialog"
                PaperProps={{
                    className: 'menu-library-course-group-dialog__paper',
                }}
            >
                <DialogTitle component="div" sx={{ padding: '24px 24px 0', flexShrink: 0 }}>
                    <DialogHeaderComponent
                        title={i18n.t('menu_library_course_group_dialog_title')}
                        onClose={onClose}
                    />
                </DialogTitle>
                <Box className="menu-library-course-group-dialog__inner">
                    {initLoading ? (
                        <MenuLibraryCourseGroupDialogSkeleton />
                    ) : (
                        <>
                            <Paper elevation={0} className="menu-library-course-group-dialog__tab-bar">
                                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                    <CourseGroupTabDndContext onDragEnd={(r) => void handleCourseGroupTabDragEnd(r)}>
                                        <CourseGroupTabDroppable droppableId="course-group-tab-row" direction="horizontal">
                                            {(dProvided) => (
                                                <Box
                                                    ref={dProvided.innerRef}
                                                    {...dProvided.droppableProps}
                                                    role="tablist"
                                                    aria-label={i18n.t('menu_library_course_group_dialog_title')}
                                                    className="menu-library-course-group-dialog__tab-tags menu-library-course-group-dialog__group-tags-row"
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        flexWrap: 'nowrap',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {courseGroup.length === 0 ? (
                                                        <Typography component="span" variant="caption" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    ) : (
                                                        courseGroup.map((g, idx) => {
                                                            const isActive = activeIdx === idx;
                                                            const dragId = `cg-${String(g.course_group_id ?? `idx-${idx}`)}`;
                                                            const canDrag =
                                                                !reorderTabLoading &&
                                                                g.course_group_id != null &&
                                                                Number(g.course_group_id) > 0;
                                                            return (
                                                                <CourseGroupTabDraggable
                                                                    key={dragId}
                                                                    draggableId={dragId}
                                                                    index={idx}
                                                                    isDragDisabled={!canDrag}
                                                                >
                                                                    {(dragProvided, snapshot) => {
                                                                        const hp = dragProvided.dragHandleProps as
                                                                            | (React.HTMLAttributes<HTMLElement> & Record<string, unknown>)
                                                                            | null
                                                                            | undefined;
                                                                        return (
                                                                            <Box
                                                                                component="span"
                                                                                ref={dragProvided.innerRef}
                                                                                {...dragProvided.draggableProps}
                                                                                {...(hp ?? {})}
                                                                                role="tab"
                                                                                aria-selected={isActive}
                                                                                tabIndex={hp?.tabIndex ?? 0}
                                                                                className={clsx(
                                                                                    'menu-library-course-group-dialog__group-tag',
                                                                                    isActive && 'menu-library-course-group-dialog__group-tag--active',
                                                                                    snapshot.isDragging &&
                                                                                        'menu-library-course-group-dialog__group-tag--dragging',
                                                                                    canDrag && 'menu-library-course-group-dialog__group-tag--draggable',
                                                                                )}
                                                                                onClick={(e) => {
                                                                                    hp?.onClick?.(e as React.MouseEvent<HTMLElement>);
                                                                                    setActiveIdx(idx);
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    hp?.onKeyDown?.(e as React.KeyboardEvent<HTMLElement>);
                                                                                    if (
                                                                                        !e.defaultPrevented &&
                                                                                        (e.key === 'Enter' || e.key === ' ')
                                                                                    ) {
                                                                                        e.preventDefault();
                                                                                        setActiveIdx(idx);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    component="span"
                                                                                    variant="body2"
                                                                                    className="menu-library-course-group-dialog__group-tag-label"
                                                                                >
                                                                                    {String(g?.course_group_name ?? '').trim() || '—'}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    className="menu-library-course-group-dialog__group-tag-remove"
                                                                                    disabled={deleteGroupLoading || addGroupLoading || reorderTabLoading}
                                                                                    aria-label={i18n.t('menu_library_course_group_delete_group')}
                                                                                    onMouseDown={(e) => {
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        e.preventDefault();
                                                                                        openDeleteGroupConfirm(idx);
                                                                                    }}
                                                                                >
                                                                                    {deleteGroupLoading && deleteGroupTargetIdx === idx ? (
                                                                                        <CircularProgress color="inherit" size={14} thickness={5} />
                                                                                    ) : (
                                                                                        <CloseFillIcon
                                                                                            className="menu-library-course-group-dialog__group-tag-remove-icon"
                                                                                            aria-hidden
                                                                                        />
                                                                                    )}
                                                                                </IconButton>
                                                                            </Box>
                                                                        );
                                                                    }}
                                                                </CourseGroupTabDraggable>
                                                            );
                                                        })
                                                    )}
                                                    {dProvided.placeholder as unknown as React.ReactNode}
                                                </Box>
                                            )}
                                        </CourseGroupTabDroppable>
                                    </CourseGroupTabDndContext>
                                    <Button
                                        type="button"
                                        variant="text"
                                        color="primary"
                                        className="menu-library-course-group-dialog__add-group-btn"
                                        disabled={addGroupLoading || reorderTabLoading || deleteGroupLoading}
                                        aria-busy={addGroupLoading || reorderTabLoading || deleteGroupLoading}
                                        startIcon={
                                            addGroupLoading ? (
                                                <CircularProgress
                                                    className="menu-library-course-group-dialog__add-group-start-icon"
                                                    color="inherit"
                                                    size={20}
                                                    thickness={4}
                                                    aria-hidden
                                                />
                                            ) : (
                                                <AddRoundedIcon className="menu-library-course-group-dialog__add-group-start-icon" />
                                            )
                                        }
                                        onClick={() => void handleAddGroup()}
                                    >
                                        {i18n.t('menu_library_course_group_add_group')}
                                    </Button>
                                </Stack>
                            </Paper>

                            {activeGroup ? (
                                <Paper elevation={0} className="menu-library-course-group-dialog__form-row">
                                    <Box className="menu-library-course-group-dialog__form-row-grid">
                                        <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                                            <Typography className="menu-library-course-group-dialog__field-label">{i18n.t('menu_library_course_group_field_name')}</Typography>
                                            <TextField
                                                hiddenLabel
                                                fullWidth
                                                size="small"
                                                value={activeGroup.course_group_name ?? ''}
                                                onChange={(e) => updateGroupField({ course_group_name: e.target.value })}
                                                InputProps={courseGroupNameInputProps}
                                                className="menu-library-course-group-dialog__field-input menu-library-course-group-dialog__field-input--name"
                                            />
                                        </Box>
                                        <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                                            <Typography className="menu-library-course-group-dialog__field-label">{i18n.t('menu_library_course_group_field_type')}</Typography>
                                            <CommonInput
                                                inputType="SELECT"
                                                hiddenLabel
                                                fullWidth
                                                size="small"
                                                value={String(activeGroup.is_must ?? 0)}
                                                items={typeSelectItems}
                                                InputProps={courseGroupTypeInputProps}
                                                SelectProps={courseGroupTypeSelectProps}
                                                className="menu-library-course-group-dialog__field-input menu-library-course-group-dialog__field-input--rounded-lg"
                                                onChange={(e) =>
                                                    updateGroupField({
                                                        is_must: Number((e.target as HTMLInputElement).value),
                                                        choose_num:
                                                            Number((e.target as HTMLInputElement).value) === 1 &&
                                                                Number(activeGroup.choose_num) < 1
                                                                ? 1
                                                                : activeGroup.choose_num,
                                                    })
                                                }
                                            />
                                        </Box>
                                        <Box className="menu-library-course-group-dialog__form-field menu-library-course-group-dialog__form-field--grid-cell">
                                            <Typography className="menu-library-course-group-dialog__field-label">{i18n.t('menu_library_course_group_field_choose_num')}</Typography>
                                            <TextField
                                                hiddenLabel
                                                fullWidth
                                                size="small"
                                                type="text"
                                                inputProps={{ inputMode: 'numeric' as const, autoComplete: 'off' }}
                                                value={chooseNumDraft}
                                                InputProps={courseGroupChooseNumInputProps}
                                                onChange={(e) => {
                                                    const raw = e.target.value.replace(/\D/g, '');
                                                    setChooseNumDraft(raw);
                                                    if (raw === '') {
                                                        return;
                                                    }
                                                    const n = parseInt(raw, 10);
                                                    if (!Number.isNaN(n) && n >= 1) {
                                                        updateGroupField({ choose_num: n });
                                                    }
                                                }}
                                                onBlur={() => {
                                                    const n = parseInt(chooseNumDraft, 10);
                                                    const final = Number.isNaN(n) || n < 1 ? 1 : n;
                                                    setChooseNumDraft(String(final));
                                                    updateGroupField({ choose_num: final });
                                                }}
                                                className="menu-library-course-group-dialog__field-input menu-library-course-group-dialog__field-input--rounded-lg"
                                            />
                                        </Box>
                                        {Number(activeGroup.is_must) === 1 ? (
                                            <>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    className="menu-library-course-group-dialog__switch-row menu-library-course-group-dialog__form-grid-slot"
                                                >
                                                    <Typography className="menu-library-course-group-dialog__switch-label">{i18n.t('menu_and_dishes_page_mandatory_full')}</Typography>
                                                    <AntSwitch
                                                        className="menu-library-course-group-dialog__form-switch"
                                                        checked={!!activeGroup.is_full_select}
                                                        onChange={(_, v) => updateGroupField({ is_full_select: v ? 1 : 0 })}
                                                    />
                                                </Stack>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    className="menu-library-course-group-dialog__switch-row menu-library-course-group-dialog__form-grid-slot"
                                                >
                                                    <Typography
                                                        component="div"
                                                        className="menu-library-course-group-dialog__switch-label menu-library-course-group-dialog__switch-label--multiline"
                                                    >
                                                        {i18n.t('menu_library_course_group_select_all_max')}
                                                    </Typography>
                                                    <AntSwitch
                                                        className="menu-library-course-group-dialog__form-switch"
                                                        checked={!!activeGroup.is_select_all}
                                                        onChange={(_, v) => updateGroupField({ is_select_all: v ? 1 : 0 })}
                                                    />
                                                </Stack>
                                            </>
                                        ) : (
                                            <>
                                                <Box className="menu-library-course-group-dialog__form-grid-slot menu-library-course-group-dialog__form-field--grid-placeholder" aria-hidden />
                                                <Box className="menu-library-course-group-dialog__form-grid-slot menu-library-course-group-dialog__form-field--grid-placeholder" aria-hidden />
                                            </>
                                        )}
                                    </Box>
                                </Paper>
                            ) : null}

                            <Paper elevation={0} className="menu-library-course-group-dialog__table-card">
                                <div className="menu-library-course-group-dialog__table-toolbar">
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                                            <Paper elevation={0} className="menu-library-course-group-dialog__count-pill">
                                                {i18n.t('menu_library_course_group_selected_count', { count: selectedRowIdx.length })}
                                            </Paper>
                                            <TextField
                                                size="small"
                                                placeholder={i18n.t('menu_library_course_group_search_placeholder')}
                                                value={searchKeyword}
                                                onChange={(e) => setSearchKeyword(e.target.value)}
                                                className="menu-library-course-group-dialog__search"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchRoundedIcon fontSize="small" sx={{ color: 'var(--fill-primary-300)' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Stack>
                                        <Stack direction="row" gap={1} flexWrap="wrap">
                                            {selectedRowIdx.length > 0 ? (
                                                <Tooltip title={i18n.t('menu_library_course_group_remove_selected')}>
                                                    <span>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            disabled={deleteSelectedLoading || deletingDetailRowId != null}
                                                            onClick={() => setBatchRemoveConfirmOpen(true)}
                                                            aria-label={i18n.t('menu_library_course_group_remove_selected')}
                                                            className="menu-library-course-group-dialog__remove-selected-btn"
                                                        >
                                                            {deleteSelectedLoading ? (
                                                                <CircularProgress color="inherit" size={18} thickness={5} />
                                                            ) : (
                                                                <BaselineDeleteForeverIcon width={24} height={24} aria-hidden />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                className="menu-library-course-group-dialog__add-dish-btn"
                                                startIcon={<AddRoundedIcon />}
                                                onClick={() => {
                                                    setDishPickerOpen(true);
                                                    if (isCloudMode) {
                                                        void onEnsureCloudPickerMenus?.();
                                                    }
                                                }}
                                                disabled={!activeGroup?.course_group_id}
                                            >
                                                {i18n.t('menu_library_course_group_add_dishes')}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </div>

                                <Box className="menu-library-course-group-dialog__table-scroll">
                                    <MenuLibraryCourseGroupDetailTable
                                        filteredDetails={filteredDetails}
                                        selectedRowIdx={selectedRowIdx}
                                        onSelectedRowIdxChange={setSelectedRowIdx}
                                        currency={currency}
                                        courseGroupId={Number(activeGroup?.course_group_id) || 0}
                                        onDetailUpdated={handleCourseDetailUpdated}
                                        onDeleteDetailRow={handleDeleteDetailRow}
                                        deletingDetailRowId={deletingDetailRowId}
                                        batchDeleteInProgress={deleteSelectedLoading}
                                        rbpStringId={rbpStringId}
                                        draftMode={isCloudMode}
                                    />
                                </Box>
                            </Paper>
                        </>
                    )}

                    <Button
                        type="button"
                        variant="contained"
                        fullWidth
                        className="menu-library-course-group-dialog__save-btn"
                        onClick={() => void handleSaveGroup()}
                        disabled={
                            isCloudMode
                                ? initLoading || saveGroupLoading
                                : !activeGroup?.course_group_id || initLoading || saveGroupLoading
                        }
                        aria-busy={saveGroupLoading}
                        startIcon={
                            saveGroupLoading ? (
                                <CircularProgress
                                    className="menu-library-course-group-dialog__save-icon menu-library-course-group-dialog__save-icon--progress"
                                    color="inherit"
                                    size={32}
                                    thickness={3}
                                    aria-hidden
                                />
                            ) : (
                                <SaveIcon className="menu-library-course-group-dialog__save-icon" aria-hidden />
                            )
                        }
                    >
                        {i18n.t('menu_library_course_group_save')}
                    </Button>
                </Box>
            </Dialog>

            <AlertDialog
                open={batchRemoveConfirmOpen}
                variant="delete"
                size="sm"
                title={i18n.t('menu_library_course_group_remove_selected')}
                bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                highlightText={i18n.t('menu_library_create_dialog_batch_delete_confirm_highlight', {
                    count: selectedRowIdx.length,
                })}
                confirmText={i18n.t('menu_library_course_group_remove_selected')}
                onCancel={() => setBatchRemoveConfirmOpen(false)}
                onConfirm={() => {
                    setBatchRemoveConfirmOpen(false);
                    void handleDeleteSelectedRows();
                }}
            />

            <AlertDialog
                open={deleteGroupConfirmOpen}
                variant="delete"
                size="sm"
                title={i18n.t('menu_and_dishes_page_delete_set_meal_area')}
                bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                highlightText={deleteGroupHighlightName}
                confirmText={i18n.t('menu_and_dishes_page_delete_set_meal_area')}
                confirmLoading={deleteGroupLoading}
                onCancel={() => {
                    if (deleteGroupLoading) {
                        return;
                    }
                    setDeleteGroupConfirmOpen(false);
                    setDeleteGroupTargetIdx(null);
                }}
                onConfirm={() => {
                    void handleDeleteGroupConfirm();
                }}
            />

            <MenuDishList
                open={dishPickerOpen}
                onCancel={() => {
                    setDishPickerOpen(false);
                    setDishPickerConfirmLoading(false);
                }}
                filterDishId={isCloudMode ? undefined : dishItemId}
                menuData={menuDataForPicker}
                /** menu-dish-list：item_type===3 为套餐，默认不展示；与套餐组可选菜一致 */
                isShowPackage={false}
                confirmLoading={dishPickerConfirmLoading}
                listLoading={isCloudMode ? cloudPickerMenusLoading : false}
                expandLoadingCloudMenuId={isCloudMode ? cloudPickerDetailLoadingId : null}
                onExpandFirstMenu={isCloudMode ? handleCloudPickerExpandFirstMenu : undefined}
                onConfirm={(_ids, rows) => {
                    void addDishesToGroup(rows);
                }}
            />
        </>
    );
}
